import type { KetoRelationship as Tuple } from 'types/pop-authz/models/KetoRelationship';

const PERMISSION_READ_EVERYONE = 'everyoneCanRead';
const PERMISSION_ADMIN = '*';
const PERMISSION_MANAGE = 'manage';
const PERMISSION_MANAGE_COMMERCIAL_ITEMS = 'manage/commercialItems';
const PERMISSION_MANAGE_CONTRACT_LENGTHS =
  'manage/commercialItems/contractLengths';
const PERMISSION_MANAGE_FEES = 'manage/commercialItems/fees';
const PERMISSION_MANAGE_MILEAGES = 'manage/commercialItems/mileages';
const PERMISSION_MANAGE_SOFT_OFFERS = 'manage/commercialItems/softOffers';
const PERMISSION_MANAGE_COMMERCIAL_SEGMENTS = 'manage/commercialSegments';
const PERMISSION_MANAGE_BRANDS = 'manage/commercialSegments/brands';
const PERMISSION_MANAGE_CLIENTS = 'manage/commercialSegments/clients';
const PERMISSION_MANAGE_CUSTOMER_TYPES =
  'manage/commercialSegments/customerTypes';
const PERMISSION_MANAGE_MARKETS = 'manage/commercialSegments/markets';
const PERMISSION_MANAGE_SALES_MODELS = 'manage/commercialSegments/salesModels';

const ROUTE_PERMISSION_OBJECT_MAP = {
  '/users': [PERMISSION_ADMIN],
  '/settings/store/commercial-items': [
    PERMISSION_READ_EVERYONE,
    PERMISSION_ADMIN,
    PERMISSION_MANAGE,
    PERMISSION_MANAGE_COMMERCIAL_ITEMS,
    PERMISSION_MANAGE_CONTRACT_LENGTHS,
    PERMISSION_MANAGE_FEES,
    PERMISSION_MANAGE_MILEAGES,
    PERMISSION_MANAGE_SOFT_OFFERS,
  ],
  '/settings/store/commercial-items/contract-lengths': [
    PERMISSION_MANAGE_CONTRACT_LENGTHS,
  ],
  '/settings/store/commercial-items/fees': [PERMISSION_MANAGE_FEES],
  '/settings/store/commercial-items/mileages': [PERMISSION_MANAGE_MILEAGES],
  '/settings/store/commercial-items/soft-offers': [
    PERMISSION_MANAGE_SOFT_OFFERS,
  ],
  '/settings/store/commercial-segments': [
    PERMISSION_READ_EVERYONE,
    PERMISSION_ADMIN,
    PERMISSION_MANAGE,
    PERMISSION_MANAGE_COMMERCIAL_SEGMENTS,
    PERMISSION_MANAGE_BRANDS,
    PERMISSION_MANAGE_CLIENTS,
    PERMISSION_MANAGE_CUSTOMER_TYPES,
    PERMISSION_MANAGE_MARKETS,
    PERMISSION_MANAGE_SALES_MODELS,
  ],
  '/settings/store/commercial-segments/brands': [PERMISSION_MANAGE_BRANDS],
  '/settings/store/commercial-segments/clients': [PERMISSION_MANAGE_CLIENTS],
  '/settings/store/commercial-segments/customer-types': [
    PERMISSION_MANAGE_CUSTOMER_TYPES,
  ],
  '/settings/store/commercial-segments/markets': [PERMISSION_MANAGE_MARKETS],
  '/settings/store/commercial-segments/sales-models': [
    PERMISSION_MANAGE_SALES_MODELS,
  ],
};

const getRoutePermissions = (route: string): string[] | undefined => {
  let permissionsExist = false;
  const permissions: string[] = [];
  const splittedRoute = route.split('/');

  for (let i = 1, n = splittedRoute.length; i < n; i += 1) {
    if (!splittedRoute[i]) {
      continue;
    }

    let path = '';

    for (let j = 1; j <= i; j += 1) {
      path += `/${splittedRoute[j]}`;
    }

    const routePermissions =
      ROUTE_PERMISSION_OBJECT_MAP[
        path as keyof typeof ROUTE_PERMISSION_OBJECT_MAP
      ];

    if (routePermissions?.length) {
      permissionsExist = true;

      for (const routePermission of routePermissions) {
        permissions.push(routePermission);
      }
    }
  }

  if (permissionsExist) {
    return Array.from(new Set(permissions));
  }
};

export const checkPermission = (path: string, userPermissions: Tuple[]) => {
  const routePermissions = getRoutePermissions(path);

  if (
    routePermissions &&
    !routePermissions.find((item) => PERMISSION_READ_EVERYONE === item)
  ) {
    const isAllowed = userPermissions.find((permission) =>
      routePermissions.includes(permission.object!),
    );

    return Boolean(!routePermissions || isAllowed);
  }

  return true;
};

export const checkPermissionWithObjects = (
  objects: string[],
  relation: string[],
  userPermissions: Tuple[],
) => {
  const isAllowed = userPermissions.find(
    (permission) =>
      objects.includes(permission.object!) &&
      relation.includes(permission.relation!),
  );

  return Boolean(isAllowed);
};

export const checkPermissionWithRelation = (
  path: string,
  userPermissions: Tuple[],
  relation: string[],
) => {
  const routePermissions = getRoutePermissions(path);

  if (routePermissions) {
    const isAllowed = userPermissions.find(
      (permission) =>
        routePermissions.includes(permission.object!) &&
        relation.includes(permission.relation!),
    );

    return Boolean(!routePermissions || isAllowed);
  }

  return true;
};
